import { useFlag } from '@openfeature/react-sdk';
import {
  type ConstrainedFlagKey,
  type FlagValue,
  type InMemoryFlagConfiguration,
  OpenFeature,
} from '@openfeature/web-sdk';

export interface FeatureFlag<T extends FlagValue = boolean> {
  readonly key: ConstrainedFlagKey<T>;
  readonly defaultValue: T;
}

// Catalog of feature flags
export const FEATURE_FLAGS = {
  reportsTab: {
    key: 'reports-tab',
    defaultValue: false,
  },
  interpretationChat: {
    key: 'report-chat',
    defaultValue: false,
  },
  warnBlanksHilic: {
    key: 'warn-blanks-hilic',
    defaultValue: false,
  },
  // Needs `reports-tab` too: the chat lives in the Interpretations tab.
  studyPreparationChat: {
    key: 'study-preparation-chat',
    defaultValue: false,
  },
  reportChatAgentSlug: {
    key: 'report-chat-agent-slug',
    defaultValue: 'report-chat',
  },
  studyPreparationAgentSlug: {
    key: 'study-preparation-agent-slug',
    defaultValue: 'study-preparation',
  },
  // Keep off until every Quant tab reads the results Parquet.
  quantParquetViews: {
    key: 'quant-parquet-views',
    defaultValue: false,
  },
} as const satisfies Record<string, FeatureFlag<boolean> | FeatureFlag<string>>;

/**
 * Read a flag from inside a React component. This is a hook (re-renders when
 * the flag value changes), so it can only be called during render. For
 * non-render code (e.g. a route `beforeLoad`) use
 * `getFeatureFlagOutsideReact` instead.
 */
export const useFeatureFlag = <T extends FlagValue>(flag: FeatureFlag<T>) =>
  useFlag(flag.key, flag.defaultValue).value;

// Resolves once provider initialization has settled — ready, failed, or
// skipped. Non-React flag reads await this so they never race provider
// startup.
let resolveFlagsSettled: () => void;
const flagsSettled = new Promise<void>((resolve) => {
  resolveFlagsSettled = resolve;
});

/** Signals that provider initialization has settled (ready, failed, or skipped). */
export const markFeatureFlagsSettled = () => {
  resolveFlagsSettled();
};

/**
 * Read a flag from outside React render (e.g. a route `beforeLoad`). Waits
 * for provider initialization to settle, so the pre-init default can never be
 * observed; if the provider failed or was skipped, resolves to the flag's
 * default. In components, prefer the `useFeatureFlag` hook so the UI reacts
 * to flag changes.
 */
export const getFeatureFlagOutsideReact = async (
  flag: FeatureFlag
): Promise<boolean> => {
  await flagsSettled;
  return OpenFeature.getClient().getBooleanValue(flag.key, flag.defaultValue);
};

/**
 * Build the in-memory provider configuration for a set of flags. Used
 * by the test fixture to shape an in-memory OpenFeature provider.
 */
export const buildFlagConfiguration = (
  flags: Record<string, FlagValue>
): InMemoryFlagConfiguration => {
  const configuration: InMemoryFlagConfiguration = {};
  for (const [flagKey, value] of Object.entries(flags)) {
    configuration[flagKey] = {
      disabled: false,
      variants: { value },
      defaultVariant: 'value',
    };
  }
  return configuration;
};
